/**
 * Hilfen für den Kochmodus: Text zum Vorlesen und Hinweise für die
 * Zeitplanung („Ofen jetzt vorheizen“, „Butter rechtzeitig herausnehmen“).
 */

/** Abkürzungen und Zeichen, die beim Vorlesen ausgeschrieben werden. */
const SPOKEN: readonly (readonly [RegExp, string])[] = [
  [/(\d)\s*°\s*C\b/g, '$1 Grad'],
  [/°C\b/g, 'Grad'],
  [/\bca\.\s*/g, 'circa '],
  [/\bz\.\s*B\.\s*/g, 'zum Beispiel '],
  [/\bggf\.\s*/g, 'gegebenenfalls '],
  [/\bMin\.(?=\s|$)/g, 'Minuten'],
  [/\bMin\b/g, 'Minuten'],
  [/\bStd\.(?=\s|$)/g, 'Stunden'],
  [/\bStd\b/g, 'Stunden'],
  [/(\d)\s*EL\b/g, '$1 Esslöffel'],
  [/(\d)\s*TL\b/g, '$1 Teelöffel'],
  [/(\d)\s*kg\b/g, '$1 Kilogramm'],
  [/(\d)\s*g\b/g, '$1 Gramm'],
  [/(\d)\s*ml\b/g, '$1 Milliliter'],
  [/(\d)\s*l\b/g, '$1 Liter'],
  [/(\d)\s*cm\b/g, '$1 Zentimeter'],
  [/\bEL\b/g, 'Esslöffel'],
  [/\bTL\b/g, 'Teelöffel'],
  [/½/g, ' einhalb'],
  [/¼/g, ' ein Viertel'],
  [/¾/g, ' drei Viertel'],
  [/⅓/g, ' ein Drittel'],
  [/⅔/g, ' zwei Drittel'],
  [/(\d)\s*[–-]\s*(\d)/g, '$1 bis $2'],
];

/** Bereitet einen Text zum Vorlesen auf: Abkürzungen aus, Zeichen in Worte. */
export function speechText(text: string): string {
  let result = text;
  for (const [pattern, replacement] of SPOKEN) {
    result = result.replace(pattern, replacement);
  }
  return result.replace(/\s+/g, ' ').trim();
}

/** Was von einem Schritt für die Zeitplanung gebraucht wird. */
export interface PlanningStep {
  readonly title: string;
  readonly instruction: string;
  readonly durationMinutes: number | null;
  readonly temperatureCelsius: number | null;
  readonly animationTemplateId?: string | null;
}

/** Zeit, die ein Ofen etwa zum Vorheizen braucht. */
export const PREHEAT_MINUTES = 15;

/** Angenommene Dauer eines Schritts ohne Angabe. */
const DEFAULT_STEP_MINUTES = 5;

/** Ist der Schritt selbst das Vorheizen? */
function isPreheatStep(step: PlanningStep): boolean {
  return (
    step.animationTemplateId === 'bake.preheat' ||
    /vorheizen|vorgeheizt/i.test(`${step.title} ${step.instruction}`)
  );
}

/** Hinweis zum Vorheizen. */
export interface PreheatHint {
  readonly temperature: number;
  /** Etwa so viele Minuten, bis der Ofen gebraucht wird. */
  readonly minutesUntil: number;
  /** Index des Schritts, der den Ofen braucht. */
  readonly stepIndex: number;
}

/**
 * Soll jetzt der Ofen vorgeheizt werden? Das ist der Fall, wenn ein späterer
 * Schritt eine Temperatur hat, bis dahin kein eigener Vorheiz-Schritt kommt
 * und die Zeit bis dahin nicht länger als das Vorheizen (plus Puffer) ist.
 */
export function preheatHint(steps: readonly PlanningStep[], current: number): PreheatHint | null {
  const currentStep = steps[current];
  if (currentStep === undefined || isPreheatStep(currentStep)) {
    return null;
  }
  // Ist der Ofen schon an, weil ein früherer Schritt vorgeheizt hat?
  for (let index = 0; index <= current; index += 1) {
    const step = steps[index];
    if (step !== undefined && (isPreheatStep(step) || step.temperatureCelsius !== null)) {
      return null;
    }
  }
  let minutes = 0;
  for (let index = current; index < steps.length; index += 1) {
    const step = steps[index] as PlanningStep;
    if (index > current && isPreheatStep(step)) {
      return null;
    }
    if (index > current && step.temperatureCelsius !== null) {
      return minutes <= PREHEAT_MINUTES + 5
        ? { temperature: step.temperatureCelsius, minutesUntil: minutes, stepIndex: index }
        : null;
    }
    minutes += step.durationMinutes ?? DEFAULT_STEP_MINUTES;
  }
  return null;
}

/** Zutaten, die vorher Zimmertemperatur annehmen sollen. */
export function warmUpIngredients(ingredients: readonly { name: string; note?: string | null }[]): string[] {
  return ingredients
    .filter((ingredient) =>
      /zimmerwarm|raumtemperatur|zimmertemperatur|weich(e|er)?\b/i.test(
        `${ingredient.name} ${ingredient.note ?? ''}`,
      ),
    )
    .map((ingredient) => ingredient.name);
}
